import Vue from 'vue';
import { CampaignService, UserService } from '@/services';
import debounce from 'lodash/debounce';
import { CONNECTION_TYPES, OBJECTIVES, CAMPAIGN_STATUS } from '@consts';
import { isValueExist, deepFreeze, allValueExist } from '@util';
import { defaultFormCreate } from './form';

const { PIXEL, FB_APP, GA, FB_PAGE, YOUTUBE_CHANNEL } = CONNECTION_TYPES;

const {
    RUNNING,
    IN_REVIEW,
    IN_PROGRESS,
    PROPOSAL_REVIEW,
    IN_REVIEW_EXPRESS,
    DEACTIVATED,
    SCHEDULED,
    DISAPPROVED,
} = CAMPAIGN_STATUS;

const UPDATE_SET = new Set([
    RUNNING,
    IN_REVIEW,
    PROPOSAL_REVIEW,
    IN_REVIEW_EXPRESS,
    SCHEDULED,
    DISAPPROVED,
]);

const LAUNCHED_SET = new Set([
    RUNNING,
    IN_REVIEW,
    PROPOSAL_REVIEW,
    DEACTIVATED,
    SCHEDULED,
    DISAPPROVED,
]);

const SIMPLE_SAVE_SET = new Set([IN_PROGRESS, DEACTIVATED]);

const ROOT_TRUE = deepFreeze({
    root: true,
});

const SET_WEBSITE_VISITORS = 'proposal/setWebsiteVisitors';

export default {
    namespaced: true,
    state: {
        campaigns: [],
        campaign: {},
        cachedCampaigns: {},
        isCardLoading: false,
        estimations: undefined,
        lastUpdated: undefined,
        googleAnalyticsBackup: undefined,
        facebookPixelBackup: undefined,
        fbAppBackup: undefined,
        deactivatingSet: new Set(),
        gaConversions: [],
        pixelConversions: [],
        fbAppConversions: [],
        coupons: [],
        totalData: undefined,
        countByTabs: undefined,
        campaignOwnerLang: 'en',
        loadings: new Set(),
        expressInProcess: false,
        searchedCampaigns: [],
    },
    mutations: {
        setCampaigns(state, { data = [], isNewData, type }) {
            if (type === 'CARD') {
                state.campaigns = isNewData
                    ? data.results
                    : [...state.campaigns, ...data.results];
                state.countByTabs = data.totalByStatus;
            } else {
                state.campaigns = data;
            }
            state.deactivatingSet.clear();
        },
        setCachedCampaigns(state, { data, key }) {
            if (key) state.cachedCampaigns[key] = data;
            else {
                state.cachedCampaigns = {};
                state.countByTabs = undefined;
            }
        },
        setCardLoading(state, value) {
            state.isCardLoading = value;
        },
        setCampaign(state, data) {
            state.campaign = data;
            const path = R.path(R.__, data);
            state.googleAnalyticsBackup = path(['intel', 'googleAnalytics']);
            state.facebookPixelBackup = path(['intel', 'facebookPixel']);
            state.fbAppBackup = path(['intel', 'fbApp']);
        },
        setBillingPlan(state, value) {
            state.campaign.billing_plan = value;
        },
        setAutoOptimization(state, value) {
            state.campaign.auto_optimization = value;
        },
        setEstimations(state, value) {
            state.estimations = value;
        },
        updateProposal(state, proposal) {
            state.lastUpdated = R.clone(proposal);
        },
        setGa(state, value) {
            state.campaign.intel = {
                ...state.campaign.intel,
                googleAnalytics: value,
            };
        },
        setDeactivatingSet(state, { value, id }) {
            var set = state.deactivatingSet;
            value ? set.add(id) : set.delete(id);
        },
        setCampaignImage(state, image) {
            if (!image) {
                return;
            }
            state.campaign.image = image;
        },
        setGAConversionList(state, value = []) {
            state.gaConversions = value;
        },
        setPixelConversionList(state, value = []) {
            state.pixelConversions = value;
        },
        setFbAppConversionList(state, value = []) {
            state.fbAppConversions = value;
        },
        setCoupons(state, coupons) {
            state.coupons = coupons;
        },
        updateIntel(state, { type, value }) {
            state.campaign.intel = R.assoc(type, value, state.campaign.intel);
        },
        setCampaignOwnerLang(state, value) {
            state.campaignOwnerLang = value;
        },
        setOptimizeOptions(state, value) {
            Vue.set(state.campaign, 'optimize_options', value);
        },
        setAssets(state, value) {
            const assets = R.pathOr([], ['campaign', 'assets'], state);

            if (!allValueExist(assets, value)) {
                return;
            }

            state.campaign.assets = R.innerJoin(R.eqProps('id'), value, assets);
        },
        updateLoadings(state, { key, value } = {}) {
            let _loadings = new Set(state.loadings);

            value ? _loadings.add(key) : _loadings.delete(key);
            state.loadings = _loadings;
        },
        setExpressInProcess(state, value) {
            state.expressInProcess = value;
        },
        setSearchedCampaigns(state, value) {
            state.searchedCampaigns = value;
        },
    },
    actions: {
        updateProposal({ state, dispatch }, proposal) {
            if (!proposal) {
                return;
            }

            var omits = R.omit(['schedule']);
            if (R.equals(omits(proposal), omits(state.campaign.proposal))) {
                return;
            }

            dispatch('proposal/updateData', proposal, ROOT_TRUE);
        },
        getCampaignsByCard({ commit, state }, payload) {
            const { isNewData, ...params } = payload;
            const key = makeKeysforCachedList(params);
            const { cachedCampaigns } = state;
            if (cachedCampaigns[key]) {
                commit('setCampaigns', {
                    data: cachedCampaigns[key],
                    isNewData,
                    type: 'CARD',
                });
            } else {
                commit('view/setLoadingAdrielActive', true, ROOT_TRUE);

                return CampaignService.query(params)
                    .then((data) => {
                        commit('setCampaigns', {
                            data,
                            isNewData,
                            type: 'CARD',
                        });
                        commit('setCachedCampaigns', { data, key });
                    })
                    .catch(console.error)
                    .finally(() =>
                        commit('view/setLoadingAdrielActive', false, ROOT_TRUE)
                    );
            }
        },
        getCampaigns({ commit }) {
            return CampaignService.query()
                .then((data) => commit('setCampaigns', { data, type: 'ALL' }))
                .catch(console.error);
        },
        getCampaign({ commit, dispatch }, id) {
            commit('view/setLoadingAdrielActive', true, ROOT_TRUE);
            CampaignService.get(id)
                .then((data) => {
                    const isExpress = R.path(['meta', 'isExpress'], data);
                    const proposalGenerated = isValueExist(data.proposal);

                    if (isExpress && !proposalGenerated) {
                        commit('setExpressInProcess', true);

                        return setTimeout(() => {
                            dispatch('getCampaign', id);
                        }, 1000 * 40);
                    }

                    commit('setExpressInProcess', false);
                    //need to fetch new campaigns
                    commit('setCampaign', data);
                    commit(
                        'proposal/setData',
                        R.prop('proposal', data),
                        ROOT_TRUE
                    );

                    dispatch(
                        'proposal/checkAppstore',
                        R.prop('proposal', data),
                        ROOT_TRUE
                    );

                    if (
                        R.pathEq(
                            ['proposal', 'meta', 'webOrApp', 'type'],
                            'form',
                            data
                        )
                    ) {
                        // if(!data.proposal.form)
                        data.form = data.proposal.form || defaultFormCreate; //for resolving disconnecting reference
                        commit(
                            'form/SET_DATA',
                            ['formCreate', R.prop('form', data)],
                            ROOT_TRUE
                        );
                    }
                })
                .catch(console.error)
                .finally(() =>
                    commit('view/setLoadingAdrielActive', false, ROOT_TRUE)
                );
        },
        copy({ commit }, { id, cb }) {
            CampaignService.copy(id)
                .then((value) => {
                    commit('setCachedCampaigns', { data: {} });
                    cb(value);
                })
                .catch(console.error);
        },
        delete({ commit, state }, { ids, cb }) {
            CampaignService.deleteCampaign(ids)
                .then(() => {
                    commit('setCachedCampaigns', { data: {} });
                    commit('setCampaigns', {
                        data: R.reject(
                            R.compose(
                                (id) => R.any(R.equals(id), ids),
                                R.prop('id')
                            ),
                            state.campaigns
                        ),
                    });
                    cb();
                })
                .catch(console.error);
        },
        updateCampaign({ dispatch }, { id, payload }) {
            return CampaignService.update(id, payload)
                .then(() =>
                    //need to fetch new campaigns
                    dispatch('getCampaigns')
                )
                .catch(console.error);
        },
        deactivateCampaign({ commit, dispatch, state }, { id, params }) {
            if (state.deactivatingSet.has(id)) {
                return;
            }
            commit('setDeactivatingSet', { value: true, id });
            CampaignService.deactivate(id)
                .then(() => {
                    commit('setCachedCampaigns', { data: {} });
                    dispatch('getCampaignsByCard', params);
                })
                .catch(console.error)
                .finally(() =>
                    commit('setDeactivatingSet', { value: false, id })
                );
        },
        requestEstimations: debounce(function ({ commit, getters }) {
            CampaignService.getEstimations(getters.campaignId, getters.proposal)
                .then((data) => commit('setEstimations', data))
                .catch(console.error);
        }, 250),
        updateTitle(_, { newValue, oldValue }) {
            if (!newValue.title.trim() || newValue.title === oldValue.title) {
                return Promise.resolve();
            }

            return CampaignService.updateTitle(
                newValue.id,
                newValue.title
            ).then(({ title }) => {
                oldValue.title = title;
                return title;
            });
        },
        updateGa({ dispatch, getters, commit }, data = {}) {
            return CampaignService.updateGa(getters.campaignId, data)
                .then(() => {
                    commit('setGa', data);
                    commit(
                        SET_WEBSITE_VISITORS,
                        {
                            type: GA,
                            value: data,
                        },
                        ROOT_TRUE
                    );

                    dispatch('getGAConversionList');
                })
                .catch(console.error);
        },
        getGAConversionList({ commit, getters }, campaignId) {
            CampaignService.listConversions({
                campaignId: campaignId || getters.campaignId,
            }).then(
                (data = []) => {
                    commit('setGAConversionList', data);
                },
                () => commit('setGAConversionList', [])
            );
        },
        getPixelConversionList({ commit, getters }, campaignId) {
            commit('updateLoadings', { key: PIXEL, value: true });

            CampaignService.listConversions({
                campaignId: campaignId || getters.campaignId,
                type: PIXEL,
            })
                .then(
                    (data = []) => commit('setPixelConversionList', data),
                    () => commit('setPixelConversionList', [])
                )
                .finally(() =>
                    commit('updateLoadings', { key: PIXEL, value: false })
                );
        },
        getFbAppConversionList({ commit, getters }, campaignId) {
            commit('updateLoadings', { key: FB_APP, value: true });

            CampaignService.listConversions({
                campaignId: campaignId || getters.campaignId,
                type: FB_APP,
            })
                .then(
                    (data = []) => commit('setFbAppConversionList', data),
                    () => commit('setFbAppConversionList', [])
                )
                .finally(() =>
                    commit('updateLoadings', { key: FB_APP, value: false })
                );
        },
        resetGa({ commit, getters }) {
            CampaignService.updateGa(
                getters.campaignId,
                getters.googleAnalyticsBackup
            )
                .then(() => commit('setGa', getters.googleAnalyticsBackup))
                .catch(console.error);
        },
        resetFacebookPixel({ commit, getters }) {
            CampaignService.updatePixel(
                getters.campaignId,
                getters.facebookPixelBackup
            )
                .then(() =>
                    commit('updateIntel', {
                        type: PIXEL,
                        value: getters.facebookPixelBackup,
                    })
                )
                .catch(console.error);
        },
        resetFbApp({ commit, getters }) {
            CampaignService.updateFbApp(getters.campaignId, getters.fbAppBackup)
                .then(() =>
                    commit('updateIntel', {
                        type: FB_APP,
                        value: getters.fbAppBackup,
                    })
                )
                .catch(console.error);
        },
        refreshCampaignImage({ commit }, id) {
            CampaignService.get(id)
                .then(({ image }) => commit('setCampaignImage', image))
                .catch(console.error);
        },
        updateFacebookPixel({ commit, getters }, data = {}) {
            return CampaignService.updatePixel(getters.campaignId, data)
                .then(() => {
                    commit('updateIntel', { type: PIXEL, value: data });
                    commit(
                        SET_WEBSITE_VISITORS,
                        {
                            type: PIXEL,
                            value: data,
                        },
                        ROOT_TRUE
                    );
                    if (!isValueExist(data)) {
                        commit(
                            'proposal/removeObjectiveByType',
                            'pixel',
                            ROOT_TRUE
                        );
                    }
                })
                .catch((err) => {
                    console.log(err);
                    throw new Error(err);
                });
        },
        updateFbApp({ commit, getters }, data = {}) {
            return CampaignService.updateFbApp(getters.campaignId, data)
                .then(() => {
                    commit('updateIntel', { type: FB_APP, value: data });
                    commit(
                        SET_WEBSITE_VISITORS,
                        {
                            type: FB_APP,
                            value: data,
                        },
                        ROOT_TRUE
                    );
                })
                .catch(console.error);
        },
        extendCampaign({ dispatch }, { id, value }) {
            return CampaignService.extend(id, value).then(() => {
                dispatch('getCampaigns');
            });
        },
        getAvailableCoupons({ commit, getters }) {
            return CampaignService.coupons(getters.campaignId)
                .then((result = []) => {
                    commit(
                        'setCoupons',
                        R.compose(
                            R.reverse,
                            R.sortBy(R.prop('created_at'))
                        )(result)
                    );
                })
                .catch(console.error);
        },
        fetchCampaignOwnerLang({ commit, getters: { userId } }) {
            UserService.fetchUser(userId, ['language']).then(({ language }) => {
                commit('setCampaignOwnerLang', language);
            });
        },
        resetIntel({ commit, state }) {
            const { facebookPixelBackup, googleAnalyticsBackup, fbAppBackup } =
                state;
            commit('updateIntel', {
                type: PIXEL,
                value: facebookPixelBackup,
            });
            commit('updateIntel', {
                type: GA,
                value: googleAnalyticsBackup,
            });
            commit('updateIntel', {
                type: FB_APP,
                value: fbAppBackup,
            });
        },
    },
    getters: {
        campaigns: R.prop('campaigns'),
        totalData: R.prop('totalData'),
        campaignStatus: R.path(['campaign', 'status']),
        countByTabs: R.prop('countByTabs'),
        isCardLoading: R.prop('isCardLoading'),
        hasCampaigns: R.pathSatisfies(R.gt(R.__, 0), ['campaigns', 'length']),
        campaign: R.prop('campaign'),
        autoOptimization: R.path(['campaign', 'auto_optimization']),
        proposal: R.path(['campaign', 'proposal']),
        estimations: R.prop('estimations'),
        campaignId: R.path(['campaign', 'id']),
        intel: R.path(['campaign', 'intel']),
        intelFacebookPage: R.path(['campaign', 'intel', FB_PAGE]),
        lastUpdated: R.prop('lastUpdated'),
        billingPlan: R.path(['campaign', 'billing_plan']),
        googleAnalytics: R.path(['campaign', 'intel', 'googleAnalytics']),
        googleAnalyticsBackup: R.prop(['googleAnalyticsBackup']),
        facebookPixelBackup: R.prop(['facebookPixelBackup']),
        isUpdate: (_, { campaignStatus }) => {
            return UPDATE_SET.has(campaignStatus);
        },
        isLaunched: (_, { campaignStatus }) => {
            return LAUNCHED_SET.has(campaignStatus);
        },
        title: R.path(['campaign', 'title']),
        campaignImage: R.path(['campaign', 'image']),
        serviceFee: R.pathOr(0.13, ['campaign', 'service_fee']),
        selectedCoupons({ coupons = [] }, { campaignId }) {
            return coupons.filter(R.propEq('campaign_id', campaignId));
        },
        couponsWithouCreditCard(_, { selectedCoupons = [] }) {
            return selectedCoupons.filter((coupon) => !coupon.need_credit_card);
        },
        needCreditCard(_, getters) {
            const coupons = R.propOr([], 'selectedCoupons', getters);
            if (!isValueExist(coupons)) {
                return true;
            }

            return coupons.every(R.prop('need_credit_card'));
        },
        meta: R.pathOr({}, ['campaign', 'meta']),
        userId: R.pathOr({}, ['campaign', 'user_id']),
        intelFbApp: R.path(['campaign', 'intel', FB_APP]),
        executed_at: R.path(['campaign', 'executed_at']),
        accountId: R.path(['campaign', 'account_id']),
        optimize_options: R.path(['campaign', 'optimize_options']),
        pixelConversions: R.prop('pixelConversions'),
        fbAppConversions: R.prop('fbAppConversions'),
        campaignAssets: R.pathOr([], ['campaign', 'assets']),
        intelConnectionStatus: R.compose(
            R.reduce(
                (acc, { type, connected }) => R.assoc(type, connected, acc),
                {}
            ),
            R.prop('campaignAssets'),
            R.nthArg(1)
        ),
        intelConnectionStatusForCheckingLaunch(
            _,
            { intelConnectionStatus },
            _2,
            rootGetters
        ) {
            let status = intelConnectionStatus;

            let omits = [];

            if (!rootGetters['proposal/fbFamilyExist']) {
                omits = [...omits, PIXEL, FB_PAGE, FB_APP];
            }

            if (
                rootGetters['proposal/campaignObjective'] !==
                OBJECTIVES.CONVERSIONS
            ) {
                omits = [...omits, PIXEL, FB_APP];
            }

            return R.omit(omits, status);
        },
        expressInProcess: R.prop('expressInProcess'),
        youtubeChannelInCampaign: R.compose(
            R.propOr({}, 'data'),
            R.find(R.propEq('type', YOUTUBE_CHANNEL)),
            R.pathOr([], ['campaign', 'assets'])
        ),
        launchedBefore(_, { campaignStatus }) {
            return LAUNCHED_SET.has(campaignStatus);
        },
        statusInSimplySavable(_, { campaignStatus }) {
            return SIMPLE_SAVE_SET.has(campaignStatus);
        },
        accountInCampaign: R.pathOr({}, ['campaign', 'account']),
        campaignCurrency(_, { accountInCampaign, campaign }, _2, rootGetters) {
            const userAccout = rootGetters['user/account'] || {};
            return (
                campaign.currency ||
                accountInCampaign.currency ||
                userAccout.currency ||
                'USD'
            );
        },
    },
};

const makeKeysforCachedList = R.compose(
    R.join('-'),
    R.map(R.join(':')),
    Object.entries
);
